<template>
  <div>
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-select
          v-model="selectedStatus"
          :items="statusOptions"
          :label="$t('courses.filterByStatus')"
          clearable
          outlined
          dense
          @change="applyFilters"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="searchText"
          :label="$t('courses.search')"
          prepend-inner-icon="mdi-magnify"
          clearable
          outlined
          dense
          @input="applyFilters"
        />
      </v-col>
    </v-row>

    <v-row class="mb-2">
      <v-col cols="12">
        <v-chip-group>
          <v-chip color="success" text-color="white" small>
            {{ $t('courses.active') }}: {{ counts.active }}
          </v-chip>
          <v-chip color="error" text-color="white" small>
            {{ $t('courses.inactive') }}: {{ counts.inactive }}
          </v-chip>
          <v-chip color="info" text-color="white" small>
            {{ $t('courses.total') }}: {{ counts.total }}
          </v-chip>
        </v-chip-group>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="filteredCourses"
      :loading="loading"
      :search="searchText"
      class="elevation-1"
      :items-per-page="15"
      :footer-props="{
        'items-per-page-options': [10, 15, 25, 50],
        'items-per-page-text': $t('general.itemsPerPage'),
      }"
    >
      <template v-slot:item.name="{ item }">
        <div class="d-flex align-center">
          <v-icon :color="item.active ? 'success' : 'error'" class="mr-2">
            {{ item.active ? 'mdi-check-circle' : 'mdi-cancel' }}
          </v-icon>
          <span :class="{ 'text--disabled': !item.active }">
            {{ item.name }}
          </span>
        </div>
      </template>

      <template v-slot:item.joinCode="{ item }">
        <div class="d-flex align-center">
          <code class="mr-2">{{ item.joinCode }}</code>
          <v-btn
            icon
            small
            @click="copyToClipboard(item.joinCode)"
          >
            <v-icon small>mdi-content-copy</v-icon>
          </v-btn>
        </div>
      </template>

      <template v-slot:item.studentCount="{ item }">
        <div class="d-flex align-center justify-center">
          <v-chip
            :color="getStudentCountColor(item.studentCount)"
            text-color="white"
            small
            class="ma-1"
          >
            {{ item.studentCount || 0 }}
          </v-chip>
        </div>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip
          :color="item.active ? 'success' : 'error'"
          :text-color="item.active ? 'white' : 'white'"
          small
        >
          <v-icon left small>
            {{ item.active ? 'mdi-check-circle' : 'mdi-cancel' }}
          </v-icon>
          {{ item.active ? $t('courses.active') : $t('courses.inactive') }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <course-status-toggle
          :course="item"
          @status-changed="handleStatusChange"
        />
      </template>
    </v-data-table>

    <v-overlay :value="loading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import courseService from '@/services/course.service';
import courseSectionMixin from '@/mixins/grade&Course';
import CourseStatusToggle from './CourseStatusToggle.vue';

export default {
  name: 'CollegeCoursesTable',
  components: {
    CourseStatusToggle,
  },
  mixins: [courseSectionMixin],
  props: {
    schoolId: {
      type: [String, Number],
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    courses: [],
    filteredCourses: [],
    selectedStatus: null,
    searchText: '',
    tableLoading: false,
    counts: {
      active: 0,
      inactive: 0,
      total: 0,
    },
  }),
  computed: {
    headers() {
      return [
        {
          text: this.$t('courses.name'),
          value: 'name',
          sortable: true,
        },
        {
          text: this.$t('courses.joinCode'),
          value: 'joinCode',
          sortable: false,
        },
        {
          text: this.$t('courses.studentCount'),
          value: 'studentCount',
          sortable: true,
          align: 'center',
        },
        {
          text: this.$t('courses.status'),
          value: 'status',
          sortable: true,
        },
        {
          text: this.$t('general.actions'),
          value: 'actions',
          sortable: false,
          align: 'center',
        },
      ];
    },
    statusOptions() {
      return [
        { text: this.$t('courses.active'), value: true },
        { text: this.$t('courses.inactive'), value: false },
      ];
    },
  },
  async mounted() {
    await this.loadCourses();
  },
  watch: {
    schoolId: {
      handler: 'loadCourses',
      immediate: false,
    },
  },
  methods: {
    async loadCourses() {
      if (!this.schoolId) {
        this.courses = [];
        this.filteredCourses = [];
        this.updateCounts();
        return;
      }
      this.tableLoading = true;
      try {
        const response = await courseService.findAllBySchoolWithStatus(this.schoolId, false);
        if (response.status === 200) {
          this.courses = response.data || [];
          this.applyFilters();
          this.updateCounts();
        }
      } catch (error) {
        console.error('Error loading courses:', error);
        this.$toast.error(this.$t('courses.errorLoadingCourses'));
      } finally {
        this.tableLoading = false;
      }
    },
    applyFilters() {
      let filtered = [...this.courses];

      if (this.selectedStatus !== null) {
        filtered = filtered.filter((course) => course.active === this.selectedStatus);
      }

      this.filteredCourses = filtered;
    },
    updateCounts() {
      this.counts = {
        active: this.courses.filter((course) => course.active).length,
        inactive: this.courses.filter((course) => !course.active).length,
        total: this.courses.length,
      };

      this.$emit('counts-updated', {
        totalCourses: this.counts.total,
        active: this.counts.active,
        total: this.counts.total,
      });
    },
    async handleStatusChange(course, newStatus) {
      try {
        const courseIndex = this.courses.findIndex((c) => c.id === course.id);
        if (courseIndex !== -1) {
          this.courses[courseIndex].active = newStatus;
          this.applyFilters();
          this.updateCounts();
        }

        this.$emit('course-updated');
        this.$toast.success(
          newStatus
            ? this.$t('courses.courseActivated')
            : this.$t('courses.courseDeactivated'),
        );
      } catch (error) {
        this.$toast.error(this.$t('courses.errorUpdatingCourse'));
        await this.loadCourses();
      }
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$toast.success(this.$t('courses.codeCopied'));
      } catch (error) {
        this.$toast.error(this.$t('courses.errorCopyingCode'));
      }
    },
    getStudentCountColor(count) {
      if (!count || count === 0) return 'grey';
      return 'green';
    },
  },
};
</script>

<style scoped>
.v-chip {
  margin: 2px;
}

code {
  background-color: #f5f5f5;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.text--disabled {
  opacity: 0.6;
}

</style>
